import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import { deleteTFU, getTFUs, postTFU, putTFU, getTicketTFUs, getTFUWidget } from '../Api/TFU/TFUApi';
import { User } from './User';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { TicketResolutionType, TicketStatus, TicketInquiryType } from './Ticket';

export enum TicketFollowUpResult {
  PENDING = 'Pending',
  DONE = 'Done'
}

export enum TicketFollowUpEscalation {
  E0 = 'E0',
  E1 = 'E1',
  E2 = 'E2'
}

export interface TFU {
  id?: string;
  dueDate: string | null;
  startedDate: string;
  result: TicketFollowUpResult;
  escalation: TicketFollowUpEscalation;
  note?: string;
  ticketId: string;
  ticketNumber: string | null;
  supplierName: string | null;
  tsdName: string | null;
  assignedTo: User;
  owner: User;
  createdAt: string;
  updatedAt: string;
  status: TicketStatus | null;
  inquiryType: TicketInquiryType | null;
  description: string | null;
  receivedDate: string | null;
  submittedToSourceDate: string | null;
  expectedResolutionDate: string | null;
  actualResolutionDate: string | null;
  resolutionType: TicketResolutionType | null;
  customerList: { id: string; sf_id: string; name: string }[] | null;
  apList: { id: string; sf_id: string; name: string }[] | null;
  ticketCreatedDate: string | null;
  latestComment: string | null;
  latestCommentAt: string | null;
  latestCommentBy: string | null;
  sourceTrackingNumber: string | null;
}

export interface TTFUWidget {
  myOverdueCount: number;
  myPendingCount: number;
  pendingCount: number;
}

const tfuSubject = new BehaviorSubject<TFU[]>([]);
const tfuDrawerSubject = new BehaviorSubject<TFU[]>([]);
const tfuWidgetSubject = new BehaviorSubject<TTFUWidget | null>(null);
const tfuLoadingSubject = new BehaviorSubject<boolean>(false);
const tfuDrawerLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyTFUsLoadingSubject = new BehaviorSubject<boolean>(false);
const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const tallyTFUsSubject = new BehaviorSubject<PaginatedResponse<TFU>>({} as PaginatedResponse<TFU>);

export const getTFUSubject = (): BehaviorSubject<TFU[]> => tfuSubject;
export const getTFUDrawerSubject = (): BehaviorSubject<TFU[]> => tfuDrawerSubject;
export const getTFULoadingSubject = (): BehaviorSubject<boolean> => tfuLoadingSubject;
export const getTFUDrawerLoadingSubject = (): BehaviorSubject<boolean> => tfuDrawerLoadingSubject;
export const getTallyTFUsSubject = (): BehaviorSubject<PaginatedResponse<TFU>> => tallyTFUsSubject;
export const getTFUWidgetSubject = (): BehaviorSubject<TTFUWidget | null> => tfuWidgetSubject;

export const fetchTFUs = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyTFUsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyTFUsLoadingSubject.next(true);

  const { data, error } = await getTFUs(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('tfu');
    tallyTFUsSubject.next(data);
  }
  if (error) addError('tfu', error.message);

  newFetchNumber === currentFetchNumber && tallyTFUsLoadingSubject.next(false);
};

export const fetchTicketTFUs = async (ticketId: string, drawer = false) => {
  const loading = drawer ? tfuDrawerLoadingSubject : tfuLoadingSubject;
  const subject = drawer ? tfuDrawerSubject : tfuSubject;
  loading.next(true);

  const { data, error } = await getTicketTFUs(ticketId);

  if (data) {
    clearError('tfu');
    subject.next(data);
  }
  if (error) addError('tfu', error.message);

  loading.next(false);
};

export const fetchTicketTFUsDrawer = async (ticketId: string) => {
  return fetchTicketTFUs(ticketId, true);
};

export const fetchTfuWidget = async () => {
  const { data, error } = await getTFUWidget();

  if (data) {
    clearError('tfu');
    tfuWidgetSubject.next(data);
  }
  if (error) addError('tfu', error.message);
};

export const createTFU = async (tfuData: TFU, drawer = false) => {
  const { data, error } = await postTFU(tfuData);

  if (data) {
    clearError('tfu');
    const subject = drawer ? tfuDrawerSubject : tfuSubject;
    const currentData = [...subject.getValue()];
    currentData.push(data);
    subject.next(currentData);
  }
  if (error) addError('tfu', error.message);
};

export const updateTFU = async (updateData: TFU, drawer = false) => {
  const { data, error } = await putTFU(updateData);

  if (data) {
    clearError('tfu');
    const subject = drawer ? tfuDrawerSubject : tfuSubject;
    const currentData = [...subject.getValue()];
    const dataIndex = currentData.findIndex((item) => item.id === data.id);
    if (dataIndex !== -1) {
      currentData[dataIndex] = data;
    } else {
      currentData.push(data);
    }
    subject.next(currentData);
  }
  if (error) addError('tfu', error.message);
};

export const removeTFU = async (id: string, drawer = false) => {
  const { error } = await deleteTFU(id);
  clearError('tfu');
  if (error) {
    addError('tfu', error.message);
  } else {
    const subject = drawer ? tfuDrawerSubject : tfuSubject;
    const currentData = [...subject.getValue()];
    const dataIndex = currentData.findIndex((item) => item.id === id);
    if (dataIndex !== -1) {
      currentData.splice(dataIndex, 1);
    }
    subject.next(currentData);
  }
};
